import type { AuthState } from "@/store/auth";
import authConfig from "@/config/auth.ts";
import { toast } from "react-toastify";

/**
Constructs a query string by encoding the given parameters and appending them to the provided API endpoint.
@param {string} apiEndpoint - The API endpoint to which the parameters will be appended.
@param {Object} params - The parameters to be encoded and added to the API endpoint.
@returns {string} - The API endpoint with the encoded parameters appended as a query string.
*/
export function paramsReducer(
  apiEndpoint: string,
  params: Record<string, any>
): string {
  const updatedParams = Object.fromEntries(
    Object.entries({ ...params }).filter(
      ([key, value]) => value !== undefined && value !== ""
    )
  );

  const queryParams = Object.entries(updatedParams)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value

          .map((v) => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`)
          .join("&");
      } else if (typeof value === "object" && value !== null) {
        return Object.entries(value)
          .map(
            ([nestedKey, nestedValue]) =>
              `${encodeURIComponent(key)}[${encodeURIComponent(nestedKey)}]=${encodeURIComponent(String(nestedValue))}`
          )
          .join("&");
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    })
    .join("&");

  if (queryParams.length > 0) {
    return `${apiEndpoint}?${queryParams}`;
  } else {
    return apiEndpoint;
  }
}

/**
 * Asserts that a session is authenticated and has a valid user.
 *
 * This function performs a type guard assertion that narrows the session type
 * to ensure it contains an authenticated state with a non-null user object.
 *
 * @param session - The authentication state to validate, which may be null or unauthenticated
 *
 * @throws {Error} Throws an error if the session is null, not authenticated, or missing a user
 *
 * @remarks
 * This function is typically used in route loaders within authenticated layouts
 * to provide TypeScript with compile-time guarantees that the session and user exist.
 * Since the parent layout redirects unauthenticated users, this assertion should
 * never throw in practice - it exists primarily for type safety.
 *
 * @example
 * ```typescript
 * export const Route = createFileRoute("/_dashboard/participations")({
 *   loader: ({ context }) => {
 *     assertAuthenticated(context.session);
 *     // TypeScript now knows session.user is defined
 *     return { user: context.session.user };
 *   },
 * });
 * ```
 */
export function assertAuthenticated(
  session: AuthState | null
): asserts session is AuthState & { user: NonNullable<AuthState["user"]> } {
  if (!session || !session.isAuthenticated || !session.user) {
    throw new Error("Not authenticated");
  }
}

/**
 * Opens a protected file (PDF) in a new window with authentication
 */
export const openProtectedFile = async (url: string): Promise<boolean> => {
  if (!url) return false;

  try {
    const token = localStorage.getItem(authConfig.storageTokenKeyName);
    if (!token) {
      console.error("No authentication token found");
      return false;
    }

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      toast.error("Impossible d'ouvrir le document.");
      return false;
    }

    const blob = await res.blob();
    const pdfBlob = new Blob([blob], { type: "application/pdf" });
    const objectURL = URL.createObjectURL(pdfBlob);

    return await new Promise((resolve) => {
      const newWindow = window.open("", "_blank");

      if (!newWindow) {
        console.error("Failed to open new window");
        URL.revokeObjectURL(objectURL);
        resolve(false);
        return;
      }
      // à modifier
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Document</title>
          <style>
            body { margin: 0; padding: 0; }
            iframe { width: 100vw; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe id="pdf-viewer" src="${objectURL}" type="application/pdf"></iframe>
        </body>
        </html>
      `);
      newWindow.document.close();

      const iframe = newWindow.document.getElementById(
        "pdf-viewer"
      ) as HTMLIFrameElement;

      if (iframe) {
        iframe.onload = () => {
          console.log("PDF loaded successfully");
          resolve(true);
        };

        iframe.onerror = () => {
          console.error("PDF failed to load");
          resolve(false);
        };
      } else {
        setTimeout(() => resolve(true), 500);
      }

      // Cleanup quand la fenêtre se ferme
      const cleanup = () => {
        URL.revokeObjectURL(objectURL);
        newWindow.removeEventListener("beforeunload", cleanup);
      };
      newWindow.addEventListener("beforeunload", cleanup);
    });
  } catch (error) {
    console.error("File access failed:", error);
    return false;
  }
};

/**
 * Downloads a protected file with authentication
 * @param {string} url - The protected file URL
 * @param {string} fallbackFilename - Default filename if not provided by server
 * @returns Promise with success status or error
 */
export const downloadProtectedFile = async (
  url: string,
  fallbackFilename = "document.pdf"
): Promise<{ error?: string }> => {
  if (!url) {
    return { error: "URL manquante" };
  }

  try {
    const token = localStorage.getItem(authConfig.storageTokenKeyName);
    if (!token) {
      console.error("No authentication token found");
      return { error: "Token d'authentification manquant" };
    }

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      console.error("Download failed");
      return { error: "Échec du téléchargement" };
    }

    const blob = await res.blob();

    const contentDisposition = res.headers.get("content-disposition");
    let filename = fallbackFilename;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);

    return {}; // Succès
  } catch (error) {
    console.error("File download failed:", error);
    return { error: "Une erreur est survenue lors du téléchargement" };
  }
};
